import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  ControlData,
  FinnairFrequentFlyerCardItem,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPhoneType,
} from '@fcom/dapi/api/models';
import { ElementActions, ElementTypes, GaContext, SelectOption } from '@fcom/common/interfaces';
import {
  CountryCode,
  EMAIL_PATTERN,
  FREQUENT_FLIER_AIRLINES,
  isNotBlank,
  mapError,
  unsubscribe,
} from '@fcom/core/index';
import { TypedFormGroup } from '@fcom/service-forms/index';
import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate/index';
import { ToasterService } from '@fcom/common/services';
import { finShare } from '@fcom/rx/index';
import { ValidationErrors } from '@fcom/common/utils';
import { formatToDateOfBirth } from '@fcom/common-booking/utils/date-utils';
import {
  PaxFormFields,
  validateFrequentFlyer,
  validatePhoneNumber,
} from '@fcom/common-booking/components/pax-details/pax-details-form-group.component';
import { DapiError } from '@fcom/dapi/index';
import {
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  ModalButtons,
  NotificationTheme,
} from '@fcom/ui-components/index';

import { numbersWithOptionCode } from '../../../utils';
import { ManageBookingService } from '../../../services';
import { PassengerForm } from './check-in-passenger-details.component';
import { PassengerWithCheckInInfo } from '../interface/check-in.interface';

@Component({
  selector: 'fin-check-in-passenger-info',
  templateUrl: 'check-in-passenger-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInPassengerInfoComponent implements OnInit, OnDestroy, OnChanges {
  readonly ModalButtons = ModalButtons;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly FREQUENT_FLYER_CARD_ERRORS = ['FREQUENT_FLYER_CARD_ADDITION', 'FREQUENT_FLYER_CARD_DELETION'];
  protected readonly ButtonPosition = ButtonPosition;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();

  editingPassenger$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Input() passenger: PassengerWithCheckInInfo;
  @Input() passengersWithoutInfants: PassengerWithCheckInInfo[];
  @Input() paxIndex: number;
  @Input() accompanyingAdult: FinnairPassengerItem;
  @Input() accompanyingInfant: FinnairPassengerItem;
  @Input() controlData$: Observable<ControlData>;
  @Input() isMainPax: boolean;
  @Input() isSaving$: Observable<boolean>;
  @Input() allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  @Input() hideGroups = false;
  @Input() trackingContext = GaContext.CHECK_IN;
  @Input() formGroup: PassengerForm;
  @Input() modifyCheckIn = false;

  @Output()
  formDirtyStatus: EventEmitter<boolean> = new EventEmitter();
  @Output()
  paxDetailsUpdating: EventEmitter<boolean> = new EventEmitter();

  frequentFlyerAirlines: SelectOption[] = FREQUENT_FLIER_AIRLINES.map(({ name, code }) => ({ name, value: code }));
  availableDays: { days$: Observable<number[]>; months: number[]; years: number[] };
  passengerGroup$: Observable<TypedFormGroup>;
  passengersToCopy: FinnairPassengerItem[] = [];
  updatingPassengers: string[] = [];

  constructor(
    private gtmService: GtmService,
    private languageService: LanguageService,
    private fb: UntypedFormBuilder,
    private manageBookingService: ManageBookingService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.updatePassengersToCopy();
    this.passengerGroup$ = combineLatest([
      this.languageService.translate<CountryCode[]>('countryCodes'),
      this.controlData$.pipe(map((controlData) => controlData?.phoneNumberRegex)),
      this.allFrequentFlyerCards$,
    ]).pipe(
      take(1),
      map(([codes, phoneNumberRegex, allFrequentFlyerCards]) => {
        const cardsForOtherPassengers = allFrequentFlyerCards.filter(
          (card) => !this.passenger.frequentFlyerCards?.some(({ cardNumber }) => cardNumber === card.cardNumber)
        );
        return this.buildPassengerFormGroup(codes, phoneNumberRegex, cardsForOtherPassengers, this.isMainPax);
      }),
      finShare()
    );

    this.subscriptions.add(
      this.passengerGroup$
        .pipe(switchMap((passengerForm) => passengerForm.valueChanges.pipe(map(() => passengerForm))))
        .subscribe((passengerForm) => this.formDirtyStatus.emit(passengerForm.dirty))
    );
  }

  getEmailValidators(isMandatory: boolean): ((control: AbstractControl) => ValidationErrors)[] {
    return isMandatory ? [Validators.required, Validators.pattern(EMAIL_PATTERN)] : [Validators.pattern(EMAIL_PATTERN)];
  }

  buildPassengerFormGroup(
    countryCodes: CountryCode[],
    phoneNumberRegex: string,
    existingCards: FinnairFrequentFlyerCardItem[],
    isMainPax: boolean
  ): TypedFormGroup {
    const paxFormGroup = this.fb.group({
      firstName: [{ value: this.passenger.firstName, disabled: true }],
      lastName: [{ value: this.passenger.lastName, disabled: true }],
      type: [{ value: this.passenger.passengerTypeCode, disabled: true }],
      email: [this.passenger.email, this.getEmailValidators(isMainPax || !!this.passenger.email)],
    }) as TypedFormGroup;

    const birthDate = this.passenger.dateOfBirth ? formatToDateOfBirth(this.passenger.dateOfBirth.toString()) : null;
    this.availableDays = birthDate
      ? {
          days$: of([parseInt(birthDate.dateOfBirth)]),
          months: [parseInt(birthDate.monthOfBirth)],
          years: [parseInt(birthDate.yearOfBirth)],
        }
      : null;
    if (birthDate) {
      paxFormGroup.addControl(
        PaxFormFields.birthDate,
        this.fb.group({ dateOfBirth: [], monthOfBirth: [], yearOfBirth: [] })
      );
      this.setBirthDateValue(paxFormGroup, birthDate.dateOfBirth, PaxFormFields.dateOfBirth);
      this.setBirthDateValue(paxFormGroup, birthDate.monthOfBirth, PaxFormFields.monthOfBirth);
      this.setBirthDateValue(paxFormGroup, birthDate.yearOfBirth, PaxFormFields.yearOfBirth);
    }

    paxFormGroup.addControl(
      PaxFormFields.phones,
      this.fb.array(
        this.passenger.phoneNumbers?.map((number, index) => {
          return this.fb.group(
            {
              [PaxFormFields.phonePrefix]: number ? numbersWithOptionCode(number, countryCodes) : '',
              [PaxFormFields.phoneNumber]: number?.number ?? '',
            },
            {
              validators: (formGroup: UntypedFormGroup) =>
                validatePhoneNumber(
                  formGroup,
                  phoneNumberRegex,
                  this.isMainPax || (index === 0 && this.passenger.phoneNumbers.length > 0)
                ),
            }
          );
        }) ?? []
      )
    );

    paxFormGroup.addControl(
      PaxFormFields.frequentFlyerCards,
      this.fb.array(
        this.passenger?.frequentFlyerCards?.map((card) =>
          this.fb.group(
            {
              [PaxFormFields.companyCode]: card?.companyCode ?? '',
              [PaxFormFields.cardNumber]: card?.cardNumber ?? '',
            },
            {
              validators: (formGroup: UntypedFormGroup): ValidationErrors =>
                validateFrequentFlyer(formGroup, existingCards),
            }
          )
        ) ?? []
      )
    );

    return paxFormGroup;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['passenger'] || changes['passengers']) {
      this.updatePassengersToCopy();
    }
  }

  togglePassengerInfo(isOpen: boolean): void {
    this.editingPassenger$.next(isOpen);
    this.gtmService.trackElement(
      `update-details_passenger-${isOpen ? 'edit' : 'cancel'}`,
      this.trackingContext,
      ElementTypes.BUTTON,
      `passenger-${this.paxIndex}`,
      ElementActions.CLICK
    );
    if (isOpen) {
      this.toasterService.removeMessage('contact-update-failed');
    } else {
      this.close();
    }
  }

  saveDetails(paxForm: UntypedFormGroup, copyToAllPassengers = false): void {
    if (!paxForm.dirty && !copyToAllPassengers) {
      this.togglePassengerInfo(false);
    } else if (!paxForm.disabled && paxForm.valid) {
      paxForm.disable();
      this.paxDetailsUpdating.emit(true);
      const { id, title, passengerTypeCode, firstName, lastName } = this.passenger;
      const { email, phones } = paxForm.value;
      const frequentFlyerCards = paxForm.value.frequentFlyerCards.filter(({ cardNumber }) => isNotBlank(cardNumber));
      const phoneNumbers = phones
        .filter(({ phoneNumber }) => isNotBlank(phoneNumber))
        .map((phoneNumber) => ({
          number: phoneNumber.phoneNumber,
          type: FinnairPhoneType.MOBILE,
          countryPrefix: phoneNumber.phonePrefix.split('|').pop(),
          countryCode: phoneNumber.phonePrefix.split('|')[0],
        }));

      const passengers = copyToAllPassengers
        ? this.passengersWithoutInfants.map((passenger) => ({
            title: passenger.title,
            id: passenger.id,
            passengerTypeCode: passenger.passengerTypeCode,
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            email,
            phoneNumbers,
            frequentFlyerCards: passenger.frequentFlyerCards,
          }))
        : [
            {
              title,
              id,
              passengerTypeCode,
              firstName,
              lastName,
              ...(isNotBlank(email) && { email }),
              phoneNumbers,
              frequentFlyerCards,
            },
          ];

      this.subscriptions.add(
        this.manageBookingService
          .updateContactDetails({
            joinFinnairPlus: false,
            passengers,
          })
          .subscribe({
            next: () => {
              this.trackSubmit('success');
              this.close();
            },
            error: (e: unknown) => {
              const { key } = mapError(e) as DapiError;
              const contentKey = this.FREQUENT_FLYER_CARD_ERRORS.includes(key)
                ? 'MMB.errors.errorUpdatingFrequentFlyer'
                : 'MMB.errors.errorContactDetails';
              this.toasterService.addMessageToQueue({
                id: 'contact-update-failed',
                contentKey,
                theme: NotificationTheme.ALERT,
              });
              this.trackSubmit('error', key);
              this.close();
            },
          })
      );
    } else {
      paxForm.markAllAsTouched();
      paxForm.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  copyContactDetailsToAll(passengerForm: UntypedFormGroup): void {
    this.saveDetails(passengerForm, true);
  }

  private updatePassengersToCopy(): void {
    this.passengersToCopy = this.passengersWithoutInfants.filter(
      (p) => p.id !== this.passenger.id && !this.passenger.email && !!p.email
    );
  }

  close(): void {
    this.paxDetailsUpdating.emit(false);
    this.editingPassenger$.next(false);
    this.formDirtyStatus.emit(false);
  }

  private setBirthDateValue(form: UntypedFormGroup, value: string, field: string): void {
    form.get(PaxFormFields.birthDate).get(field).setValue(value);
    form.get(PaxFormFields.birthDate).get(field).disable({ onlySelf: true });
  }

  private trackSubmit(event: string, errorKey: string = undefined) {
    this.gtmService.trackElement(
      `update-details_passenger-${event}`,
      this.trackingContext,
      ElementTypes.FORM,
      errorKey ? `ERROR::${errorKey}` : undefined,
      ElementActions.SUBMIT
    );
  }
}
